import * as React from "react";
import type { Language } from "../../lib/api/localization";
import type { CertificationTraining } from "../../lib/api/certifications";
import {
  CourseTrainingDetail,
  type CourseTrainingEditorState,
  type TrainingDraftSnapshot,
} from "./CourseTrainingDetail";
import * as styles from "./ModuleDetailPage.css";

const SLIDE_MS = 480;

type SlideDirection = "forward" | "back";

type OutgoingPanel = {
  training: CertificationTraining;
  index: number;
  draft: TrainingDraftSnapshot | null;
  direction: SlideDirection;
};

export function CourseTrainingDetailSlider({
  training,
  index,
  accessToken,
  onSaved,
  onStateChange,
  draft,
  onDraftChange,
  languages,
  defaultLang,
  defsByLang,
  locReady,
  remoteRevision,
  trainingRemoteRevision,
  ensureLangDefinitions,
  selectedQuestionId,
  onSelectedQuestionIdChange,
}: {
  training: CertificationTraining;
  index: number;
  accessToken: string;
  onSaved: (training: CertificationTraining) => void;
  onStateChange?: (state: CourseTrainingEditorState | null) => void;
  draft?: TrainingDraftSnapshot | null;
  onDraftChange?: (
    trainingId: string,
    snapshot: TrainingDraftSnapshot,
    changeCount: number,
  ) => void;
  languages: Language[];
  defaultLang: string;
  defsByLang: Record<string, Record<string, string>>;
  locReady: boolean;
  remoteRevision: number;
  trainingRemoteRevision: number;
  ensureLangDefinitions: (lang: string) => Promise<Record<string, string>>;
  selectedQuestionId?: string | null;
  onSelectedQuestionIdChange?: (questionId: string | null) => void;
}) {
  const prevIndexRef = React.useRef(index);
  const prevTrainingRef = React.useRef(training);
  const prevDraftRef = React.useRef(draft ?? null);
  const [outgoing, setOutgoing] = React.useState<OutgoingPanel | null>(null);
  const [enterDirection, setEnterDirection] = React.useState<SlideDirection | null>(null);
  const [reduceMotion, setReduceMotion] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  React.useEffect(() => {
    if (training.id === prevTrainingRef.current.id) {
      prevIndexRef.current = index;
      prevDraftRef.current = draft ?? null;
      return;
    }

    const direction: SlideDirection =
      index >= prevIndexRef.current ? "forward" : "back";

    if (!reduceMotion) {
      setOutgoing({
        training: prevTrainingRef.current,
        index: prevIndexRef.current,
        draft: prevDraftRef.current,
        direction,
      });
      setEnterDirection(direction);
    } else {
      setOutgoing(null);
      setEnterDirection(null);
    }

    prevTrainingRef.current = training;
    prevIndexRef.current = index;
    prevDraftRef.current = draft ?? null;
  }, [training, index, draft, reduceMotion]);

  React.useEffect(() => {
    if (!outgoing) return;
    const timer = window.setTimeout(() => {
      setOutgoing(null);
      setEnterDirection(null);
    }, SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing]);

  const enterClass =
    enterDirection === "forward"
      ? styles.trainingDetailSlideInFromRight
      : enterDirection === "back"
        ? styles.trainingDetailSlideInFromLeft
        : undefined;

  const exitClass =
    outgoing?.direction === "forward"
      ? styles.trainingDetailSlideOutToLeft
      : styles.trainingDetailSlideOutToRight;

  const locProps = {
    languages,
    defaultLang,
    defsByLang,
    locReady,
    remoteRevision,
    trainingRemoteRevision,
    ensureLangDefinitions,
  };

  return (
    <div className={styles.trainingDetailSlider}>
      {outgoing ? (
        <div
          className={[styles.trainingDetailSlidePanel, styles.trainingDetailSlideOutgoing, exitClass]
            .filter(Boolean)
            .join(" ")}
          aria-hidden
        >
          <CourseTrainingDetail
            training={outgoing.training}
            index={outgoing.index}
            accessToken={accessToken}
            onSaved={onSaved}
            draft={outgoing.draft}
            active={false}
            {...locProps}
          />
        </div>
      ) : null}

      <div
        key={training.id}
        className={[styles.trainingDetailSlidePanel, enterClass].filter(Boolean).join(" ")}
      >
        <CourseTrainingDetail
          training={training}
          index={index}
          accessToken={accessToken}
          onSaved={onSaved}
          onStateChange={onStateChange}
          draft={draft}
          onDraftChange={onDraftChange}
          active
          selectedQuestionId={selectedQuestionId}
          onSelectedQuestionIdChange={onSelectedQuestionIdChange}
          {...locProps}
        />
      </div>
    </div>
  );
}
